/**
 * Narrativa del juego: historia introductoria (se ve una vez al empezar) y
 * los "briefings" — qué ha pasado en cada lugar — que se muestran antes de
 * entrar a investigar cada distrito.
 */

/** Paneles de la introducción (se pasan con Siguiente / se puede saltar). */
export const INTRO: { title: string; body: string }[] = [
  {
    title: "Villa Segura",
    body: "Esto es <b>Villa Segura</b>… o lo era. Una ciudad como otra cualquiera: obras, fábricas, oficinas, laboratorios. Gente que sale de casa por la mañana y quiere volver entera por la noche.",
  },
  {
    title: "Demasiadas casualidades",
    body: "Pero algo ha cambiado. En los últimos meses los accidentes se acumulan: una barandilla que faltaba, una máquina sin resguardo, un cable pelado. «Casualidades», dicen. <b>Demasiadas casualidades.</b>",
  },
  {
    title: "¿Quién soy?",
    body: "Yo soy <b>EPI</b>. Equipo de Protección Individual, para los amigos. No soy policía ni inspector de carrera: soy quien <b>mira lo que los demás prefieren no ver</b>.",
  },
  {
    title: "La lista",
    body: "En cada sitio donde «pasa algo» aparece una <b>tarjeta con un número</b>. No son casualidades: son una lista. Y al final de la lista, una <b>firma</b>. Voy a reunirla, caso por caso.",
  },
  {
    title: "Manos a la obra",
    body: "Empieza por <b>Obras del Norte</b>. Habla con la gente, observa, mide… y sobre todo: <b>no preguntes qué harías. Hazlo.</b> Vamos.",
  },
];

/**
 * Los aficionados: un bético y un sevillista en la plaza. Homenaje al pueblo
 * andaluz y su jerga, con el clásico piqué de buen rollo. Al pasar cerca se lee
 * su charla; al hablar con EPI, mezclan la coña con una batallita real de
 * seguridad laboral (con su moraleja).
 */

/** Charla ambiental que se lee al acercarse (b = bético, s = sevillista). */
export const FAN_BANTER: { who: "b" | "s"; text: string }[] = [
  { who: "b", text: "Illo, este año el Betis se va a Europa, apúntalo." },
  { who: "s", text: "¿Europa? Compare, si el Betis ve Europa es de pasá, camino de Nervión." },
  { who: "b", text: "Envidia, que es lo que tenéis. ¡Viva er Beti manque pierda!" },
  { who: "s", text: "Nosotros con las Uefas ya no sabemos ni dónde ponerlas, mi arma." },
  { who: "b", text: "Palma con palma, pero el ambiente del Villamarín no lo tiene nadie." },
  { who: "s", text: "Eso te lo doy, quillo. Pero el domingo os ganamos igual." },
  { who: "b", text: "Apostamos un pescaíto frito a que no." },
  { who: "s", text: "Hecho, y que gane el mejor... o sea, nosotros." },
  { who: "b", text: "Anda, échate p'allá, que viene EPI a ver si trabajamos seguro." },
];

/** Batallitas al hablar con EPI (e = EPI, b = Manolo/bético, s = Paco/sevillista). */
export const FAN_TALKS: { who: "e" | "b" | "s"; text: string }[][] = [
  [
    { who: "e", text: "Buenas, ¿ustedes quiénes son?" },
    { who: "b", text: "Manolo, del Betis de toda la vida. Y este pesao de aquí, Paco, del Sevilla." },
    { who: "s", text: "Cuarenta años currando juntos en la Fábrica 13, illo. Entramos de chavales el mismo día." },
    { who: "b", text: "Rivales en el fútbol, hermanos en todo lo demás. Y en el tajo... lo hemos visto todo." },
    { who: "s", text: "Máquinas sin resguardo, prisas, jefes de esos que 'ya lo arreglamos mañana'." },
    { who: "b", text: "Por eso nos sentamos aquí a contarte las cosas, EPI: pa que tú no las repitas." },
    { who: "e", text: "Pues soy todo oídos. De los dos aprendo." },
  ],
  [
    { who: "e", text: "Buenas, ¿de qué habláis?" },
    { who: "b", text: "De fútbol, EPI, de lo importante. ¿Y tú de qué eres?" },
    { who: "e", text: "Yo soy de la seguridad. En el fútbol no me mojo." },
    { who: "s", text: "Listo el EPI. Mira, yo fui operario quince años. Un jefe quitó el resguardo de una prensa 'pa ir más rápido'." },
    { who: "s", text: "A un compañero le pilló la mano. Aún me acuerdo del grito. El resguardo no estorba, illo: te salva los dedos." },
    { who: "b", text: "Y no era mala gente el jefe, ¿eh? Era la prisa. La prisa es la que lisia." },
    { who: "e", text: "Lo apunto en el cuaderno. Gracias por contarlo." },
  ],
  [
    { who: "b", text: "EPI, ¿sabes cómo me destrocé la rodilla? En el almacén." },
    { who: "b", text: "Un palé tirao en mitad del paso, con aceite. Iba con una caja, no lo vi... y al suelo. Un mes de baja." },
    { who: "s", text: "Y lo peor: llevaba semanas ahí. 'Ya lo quitará alguien', decían. Ese 'alguien' no existe, compare." },
    { who: "e", text: "Orden y limpieza. Parece tontería y es de lo que más lesiona." },
    { who: "b", text: "Como el Betis en la Copa: parece tontería y te da el disgusto." },
  ],
  [
    { who: "s", text: "Oye EPI, tú que eres el equipo de protección... ¿la gente te usa?" },
    { who: "e", text: "Menos de lo que debería." },
    { who: "b", text: "Yo tenía un compañero que subía al andamio sin arnés. 'Que soy un gato', decía." },
    { who: "b", text: "Un día resbaló. Se salvó porque cayó sobre unos sacos. Los gatos tienen siete vidas; nosotros, una." },
    { who: "s", text: "Desde entonces el tío se pone hasta el casco pa ir al baño." },
    { who: "e", text: "Mejor pasarse que quedarse corto. Buena historia." },
  ],
  [
    { who: "s", text: "En una fábrica de envases estuve yo. Ruido to'l día, un escándalo." },
    { who: "s", text: "Nadie daba tapones. 'Ya te acostumbras', decían. Y me acostumbré... a no oír bien. La parienta me habla y yo '¿eh?'." },
    { who: "b", text: "Por eso no te enteras de que el Betis va primero, illo." },
    { who: "s", text: "Primero de la cola, será. Pero en serio, EPI: el oído no avisa. Cuando lo notas, ya está." },
    { who: "e", text: "Daño silencioso. Protección auditiva siempre por encima del límite. Bien traído." },
  ],
];

/** Briefing por distrito (qué ha pasado). Solo los jugables tienen incidente. */
export const BRIEFINGS: Record<string, { title: string; body: string }> = {
  obras: {
    title: "Obras del Norte",
    body: "Un trabajador cayó desde el forjado. El parte dice «resbaló y cayó»… pero la <b>barandilla del borde había desaparecido</b> y la escalera baila. Averigua qué pasó de verdad.",
  },
  fabrica: {
    title: "Fábrica 13",
    body: "La prensa 07 casi se lleva una mano. Le falta el <b>resguardo</b> y la <b>seta de emergencia está anulada con cinta</b>. Alguien decidió que la seguridad frenaba la producción.",
  },
  labo: {
    title: "Laboratorios Nova",
    body: "Un trasvase salió mal: vapores en el aire, una <b>botella sin etiquetar</b>, guantes deshechos. La <b>extracción estaba apagada</b>. ¿Prisa, descuido… o recorte?",
  },
  torre: {
    title: "Torre Central",
    body: "Turno de noche. Bajas por dolores de cuello y muñeca, agotamiento que no se va. Cambiaron las sillas, quitaron las persianas y <b>encadenaron los turnos</b>. Aquí no hay sangre, pero hay daño.",
  },
  gris: {
    title: "Distrito Gris",
    body: "«Nadie vio nada.» Una persona del equipo aislada, sobrecargada, con «bromas» que no lo son. El <b>protocolo de acoso desapareció del tablón</b>. Escucha con cuidado: no es culpa de quien lo sufre.",
  },
  central: {
    title: "Central Eléctrica",
    body: "Un cuadro <b>abierto y en tensión</b>, un charco justo debajo, un cable pelado. Intervino quien no debía. Aquí se cierra la lista… y por fin aparece la <b>firma</b>.",
  },
  poligono: {
    title: "Polígono Logístico",
    body: "Un almacén a destajo: cajas que se suben <b>a pulso</b>, una estantería a reventar y sin anclar, una carretilla que circula <b>entre la gente</b> y un charco de aceite tapado con un cartón. Aquí nadie se rompe de golpe: se rompe despacio.",
  },
  bomberos: {
    title: "Parque de Bomberos",
    body: "Una nave con un <b>conato</b> en una papelera, la <b>salida de emergencia con candado</b>, un extintor caducado, una regleta a punto de arder y el plan de evacuación lleno de polvo. Aquí no se salva por valentía: se salva por <b>minutos</b> y organización.",
  },
  hospital: {
    title: "Hospital San EPI",
    body: "Un compañero <b>en el suelo</b> junto a un cuadro que sigue en tensión, gente que no sabe qué hacer, un botiquín vacío y un DESA que nadie sabe usar. Los <b>primeros minutos</b> lo son todo… si actúas en orden: <b>Proteger, Avisar, Socorrer</b>.",
  },
  hotel: {
    title: "Hotel Mirador",
    body: "Cocina en hora punta: la <b>freidora</b> con un cazo de agua al lado, <b>lejía y amoniaco</b> a punto de mezclarse, el suelo <b>grasiento</b> y cuchillos sueltos. En hostelería el accidente no llega de golpe: se cocina <b>a fuego lento</b>.",
  },
};
